import { useState } from 'react';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.service || !formData.message) {
      alert('必須項目を入力してください。');
      return;
    }

    if (formData.message.length > 500) {
      alert('メッセージは500文字以内で入力してください。');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/submit/contact-form', {
        method: 'POST',
        body: new URLSearchParams(formDataToSend as any)
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          company: '',
          email: '',
          phone: '',
          service: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <main>
        {/* ヒーローセクション */}
        <section className="bg-gradient-to-r from-blue-900 to-blue-700 text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">お問い合わせ</h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              運送・物流サービスに関するご相談やお見積もりなど、お気軽にお問い合わせください
            </p>
          </div>
        </section>

        {/* お問い合わせ情報セクション */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* 電話でのお問い合わせ */}
              <div className="bg-white rounded-xl shadow-lg p-8 text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-phone-line text-2xl text-blue-900"></i>
                </div>
                <h3 className="text-xl font-bold text-blue-900 mb-4">お電話でのお問い合わせ</h3>
                <p className="text-gray-600 mb-4">お急ぎの場合はお電話でお気軽にご連絡ください</p>
                <a 
                  href="tel:090-9801-4659" 
                  className="text-2xl font-bold text-blue-900 hover:text-blue-700 transition-colors duration-200 cursor-pointer"
                >
                  090-9801-4659
                </a>
                <p className="text-sm text-gray-500 mt-2">営業時間: 8:00〜17:00（土日定休）</p>
              </div>

              {/* メールでのお問い合わせ */}
              <div className="bg-white rounded-xl shadow-lg p-8 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-mail-line text-2xl text-green-700"></i>
                </div>
                <h3 className="text-xl font-bold text-blue-900 mb-4">メールでのお問い合わせ</h3>
                <p className="text-gray-600 mb-4">詳細な資料やお見積もりをご希望の方はメールで</p>
                <a 
                  href="mailto:sunfield.53@gmail.com" 
                  className="text-lg font-semibold text-green-700 hover:text-green-600 transition-colors duration-200 cursor-pointer break-all"
                >
                  sunfield.53@gmail.com
                </a>
                <p className="text-sm text-gray-500 mt-2">24時間受付（返信は営業時間内）</p>
              </div>

              {/* 所在地 */}
              <div className="bg-white rounded-xl shadow-lg p-8 text-center">
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-map-pin-line text-2xl text-yellow-600"></i>
                </div>
                <h3 className="text-xl font-bold text-blue-900 mb-4">所在地</h3>
                <p className="text-gray-600 mb-2">本社</p>
                <address className="text-sm text-gray-700 not-italic mb-4">
                  〒612-8412<br />
                  京都府京都市伏見区竹田藁屋町45<br />
                  SOUBI BLD 2A
                </address>
                <p className="text-gray-600 mb-2 font-semibold">営業所</p>
                <address className="text-sm text-gray-700 not-italic">
                  京都府八幡戸津東代64-1
                </address>
              </div>
            </div>
          </div>
        </section>

        {/* よくある質問セクション */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-blue-900 mb-4">よくある質問</h2>
              <p className="text-gray-600">お問い合わせの前に、よくある質問をご確認ください</p>
            </div>

            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-blue-900 mb-3">
                  <i className="ri-question-line mr-2"></i>
                  見積もりは無料ですか？
                </h3>
                <p className="text-gray-700">
                  はい、お見積もりは無料で承っております。運送内容、距離、荷物の種類などを詳しくお聞かせいただければ、正確なお見積もりをご提示いたします。
                </p>
              </div>

              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-blue-900 mb-3">
                  <i className="ri-question-line mr-2"></i>
                  当日配送は可能ですか？
                </h3>
                <p className="text-gray-700">
                  当日配送も承っております。ただし、車両の空き状況や配送エリアによって対応できない場合もございますので、まずはお電話でご相談ください。
                </p>
              </div>

              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-blue-900 mb-3">
                  <i className="ri-question-line mr-2"></i>
                  どのような荷物でも運送できますか？
                </h3>
                <p className="text-gray-700">
                  一般的な荷物から精密機器、食品まで幅広く対応しております。ただし、危険物や法的に運送が制限されている物品については事前にご相談ください。
                </p>
              </div>

              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-blue-900 mb-3">
                  <i className="ri-question-line mr-2"></i>
                  定期配送の契約は可能ですか？
                </h3>
                <p className="text-gray-700">
                  はい、定期配送契約も承っております。月単位、週単位など、お客様のニーズに合わせた配送スケジュールをご提案いたします。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTAセクション */}
        <section className="py-16 bg-blue-900 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold mb-6">お気軽にご相談ください</h2>
            <p className="text-xl text-blue-100 mb-8">
              運送・物流に関するお悩みやご要望がございましたら、専門スタッフが丁寧にご対応いたします
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:090-9801-4659"
                className="bg-yellow-400 text-blue-900 px-8 py-4 rounded-full font-bold text-lg hover:bg-yellow-300 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line mr-2"></i>
                今すぐお電話
              </a>
              <a
                href="mailto:sunfield.53@gmail.com"
                className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white hover:text-blue-900 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-mail-line mr-2"></i>
                メールで相談
              </a>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
